//! Excel's Page Break Preview drawn over the grid.
//!
//! Everything the sheet won't print is shaded, the print area gets a heavy
//! border, automatic breaks are dashed and manual breaks are solid, and each
//! page carries a `Page N` watermark. The geometry comes from the core's
//! pagination (see `pageBreaks`), so what is drawn is what will print.

import { events } from '@/app/events/events';
import { generatedTextures } from '@/app/gridGL/generateTextures';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { pageBreaks } from '@/app/gridGL/UI/pageBreaks/pageBreaks';
import { colors } from '@/app/theme/colors';
import { sharedEvents } from '@/shared/sharedEvents';
import { BitmapText, Container, Graphics, type Rectangle } from 'pixi.js';

const AUTOMATIC_BREAK_WIDTH = 2;
const MANUAL_BREAK_WIDTH = 3;
const PRINT_AREA_BORDER_WIDTH = 4;
const DRAG_PREVIEW_WIDTH = 3;
const SHADE_ALPHA = 0.5;

/** Watermark size in screen pixels; scaled down as the user zooms out. */
const PAGE_LABEL_FONT_SIZE = 64;
const PAGE_LABEL_ALPHA = 0.16;

export class PageBreakPreview extends Container {
  private graphics: Graphics;
  private labels: Container<BitmapText>;

  dirty = true;

  constructor() {
    super();
    this.graphics = this.addChild(new Graphics());
    this.labels = this.addChild(new Container<BitmapText>());

    events.on('pageBreaks', this.setDirty);
    events.on('gridSettings', this.setDirty);
    sharedEvents.on('changeThemeAppearanceMode', this.setDirty);
    sharedEvents.on('changeThemeAccentColor', this.setDirty);
  }

  destroy() {
    events.off('pageBreaks', this.setDirty);
    events.off('gridSettings', this.setDirty);
    sharedEvents.off('changeThemeAppearanceMode', this.setDirty);
    sharedEvents.off('changeThemeAccentColor', this.setDirty);
    super.destroy();
  }

  private setDirty = () => {
    this.dirty = true;
    pixiApp.setViewportDirty();
  };

  update = (viewportDirty: boolean) => {
    pageBreaks.ensurePagination();
    if (!this.dirty && !viewportDirty) return;
    this.dirty = false;

    this.graphics.clear();
    this.labels.children.forEach((label) => (label.visible = false));

    const printRect = pageBreaks.getPrintRect();
    const printAreaRect = pageBreaks.getPrintAreaRect();
    if (!printRect || !printAreaRect) {
      this.visible = false;
      return;
    }
    this.visible = true;

    this.drawShading(printRect);
    this.drawBreaks(printRect);
    this.drawPrintAreaBorder(printAreaRect);
    this.drawPageLabels();
    this.drawDragPreview(printRect);
  };

  /** The line that tracks the pointer while a break is being dragged. */
  private drawDragPreview(printRect: Rectangle) {
    const position = pageBreaks.getPreviewPosition();
    if (position === undefined || !pageBreaks.preview) return;

    const bounds = pixiApp.viewport.getVisibleBounds();
    this.graphics.lineStyle({
      width: DRAG_PREVIEW_WIDTH / pixiApp.viewport.scale.x,
      color: content.accentColor,
      alignment: 0.5,
    });
    if (pageBreaks.preview.vertical) {
      this.graphics.moveTo(position, Math.min(printRect.top, bounds.top));
      this.graphics.lineTo(position, Math.max(printRect.bottom, bounds.bottom));
    } else {
      this.graphics.moveTo(Math.min(printRect.left, bounds.left), position);
      this.graphics.lineTo(Math.max(printRect.right, bounds.right), position);
    }
    this.graphics.lineStyle(0);
  }

  /** Grays out everything the sheet won't print, as Excel does. */
  private drawShading(printRect: Rectangle) {
    const bounds = pixiApp.viewport.getVisibleBounds();
    this.graphics.beginFill(colors.gridBackgroundOutOfBounds, SHADE_ALPHA);

    if (printRect.left > bounds.left) {
      this.graphics.drawRect(bounds.left, bounds.top, printRect.left - bounds.left, bounds.height);
    }
    if (printRect.right < bounds.right) {
      this.graphics.drawRect(printRect.right, bounds.top, bounds.right - printRect.right, bounds.height);
    }

    // the horizontal bands are clipped to the print area's columns so the
    // vertical bands above aren't painted twice
    const left = Math.max(bounds.left, printRect.left);
    const right = Math.min(bounds.right, printRect.right);
    if (right > left) {
      if (printRect.top > bounds.top) {
        this.graphics.drawRect(left, bounds.top, right - left, printRect.top - bounds.top);
      }
      if (printRect.bottom < bounds.bottom) {
        this.graphics.drawRect(left, printRect.bottom, right - left, bounds.bottom - printRect.bottom);
      }
    }

    this.graphics.endFill();
  }

  private drawBreaks(printRect: Rectangle) {
    const color = content.accentColor;
    const scale = pixiApp.viewport.scale.x;

    for (const line of pageBreaks.getColumnBreaks()) {
      this.graphics.lineStyle({
        width: (line.manual ? MANUAL_BREAK_WIDTH : AUTOMATIC_BREAK_WIDTH) / scale,
        color,
        alignment: 0.5,
        ...(line.manual ? {} : { texture: generatedTextures.dashedVertical }),
      });
      this.graphics.moveTo(line.position, printRect.top);
      this.graphics.lineTo(line.position, printRect.bottom);
    }

    for (const line of pageBreaks.getRowBreaks()) {
      this.graphics.lineStyle({
        width: (line.manual ? MANUAL_BREAK_WIDTH : AUTOMATIC_BREAK_WIDTH) / scale,
        color,
        alignment: 0.5,
        ...(line.manual ? {} : { texture: generatedTextures.dashedHorizontal }),
      });
      this.graphics.moveTo(printRect.left, line.position);
      this.graphics.lineTo(printRect.right, line.position);
    }
  }

  /**
   * The border around the print area, which is also where its edges can be
   * grabbed to resize it. Print titles outside the area print but are not part
   * of it, so the border follows the area rather than the shaded region.
   */
  private drawPrintAreaBorder(printAreaRect: Rectangle) {
    this.graphics.lineStyle({
      width: PRINT_AREA_BORDER_WIDTH / pixiApp.viewport.scale.x,
      color: content.accentColor,
      alignment: 0.5,
    });
    this.graphics.drawRect(printAreaRect.x, printAreaRect.y, printAreaRect.width, printAreaRect.height);
    this.graphics.lineStyle(0);
  }

  private drawPageLabels() {
    const inverseScale = 1 / pixiApp.viewport.scale.x;
    const bounds = pixiApp.viewport.getVisibleBounds();
    let index = 0;

    for (const { rect, pageNumber } of pageBreaks.getPageRects()) {
      // culling matters here: a fit-to-width sheet can have hundreds of pages
      if (
        rect.right < bounds.left ||
        rect.left > bounds.right ||
        rect.bottom < bounds.top ||
        rect.top > bounds.bottom
      ) {
        continue;
      }

      const label = this.getLabel(index++);
      label.visible = true;
      label.text = `Page ${pageNumber}`;
      label.tint = content.accentColor;
      label.position.set(rect.x + rect.width / 2, rect.y + rect.height / 2);

      // never let a watermark spill outside its page
      label.scale.set(1);
      const fit = Math.min(1, (rect.width * 0.8) / (label.width * inverseScale));
      label.scale.set(inverseScale * fit);
    }
  }

  private getLabel(index: number): BitmapText {
    const existing = this.labels.children[index];
    if (existing) return existing;

    const label = this.labels.addChild(new BitmapText('', { fontName: 'OpenSans', fontSize: PAGE_LABEL_FONT_SIZE }));
    label.anchor.set(0.5);
    label.alpha = PAGE_LABEL_ALPHA;
    return label;
  }
}
